/* Offline support: a read cache and a write outbox.
 *
 * Mining sites in Zambia routinely have weak or absent data coverage, so the
 * app has to stay useful while the network is gone and reconcile itself when
 * it returns. Two independent halves do that:
 *
 *   READ CACHE  — every successful GET is written to IndexedDB keyed by its
 *   path. When a later GET fails because the network is unavailable, the
 *   cached copy is served instead, marked with how old it is, so screens open
 *   with real (if stale) data instead of an error.
 *
 *   OUTBOX      — a write attempted with no network is stored and replayed,
 *   oldest first, as soon as connectivity returns. Each entry carries a
 *   client-generated id that goes to the server as an idempotency key, so a
 *   replay that the server already applied cannot create a duplicate.
 *
 * IndexedDB rather than localStorage: it is asynchronous (so a large cache
 * never blocks the UI thread), survives far more data, and is available to
 * the service worker if we ever want background sync. Every call is wrapped
 * so that a browser with IndexedDB disabled degrades to "no cache" rather
 * than a crash.
 */

const DB_NAME = 'ssm-offline'
const DB_VERSION = 1
const STORE_CACHE = 'reads'
const STORE_OUTBOX = 'outbox'

/** Cached reads older than this are treated as too stale to show. */
export const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

export interface CachedRead {
  path: string
  data: unknown
  savedAt: number
}

export interface OutboxEntry {
  id: string
  path: string
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body: unknown
  queuedAt: number
  attempts: number
  lastError?: string
  /** Human-readable summary for the pending list, e.g. "Production log". */
  label?: string
}

let dbPromise: Promise<IDBDatabase | null> | null = null

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null)
      const req = indexedDB.open(DB_NAME, DB_VERSION)
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(STORE_CACHE)) {
          db.createObjectStore(STORE_CACHE, { keyPath: 'path' })
        }
        if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
          db.createObjectStore(STORE_OUTBOX, { keyPath: 'id' })
        }
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
      req.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
  return dbPromise
}

function tx<T>(store: string, mode: IDBTransactionMode,
               run: (s: IDBObjectStore) => IDBRequest): Promise<T | null> {
  return openDb().then((db) => {
    if (!db) return null
    return new Promise<T | null>((resolve) => {
      try {
        const t = db.transaction(store, mode)
        const req = run(t.objectStore(store))
        req.onsuccess = () => resolve(req.result as T)
        req.onerror = () => resolve(null)
        t.onabort = () => resolve(null)
      } catch {
        resolve(null)
      }
    })
  })
}

/* ── Read cache ─────────────────────────────────────────────────────────── */

export async function cacheRead(path: string, data: unknown): Promise<void> {
  await tx(STORE_CACHE, 'readwrite', (s) =>
    s.put({ path, data, savedAt: Date.now() } satisfies CachedRead))
}

export async function readCached(path: string): Promise<CachedRead | null> {
  const hit = await tx<CachedRead>(STORE_CACHE, 'readonly', (s) => s.get(path))
  if (!hit) return null
  if (Date.now() - hit.savedAt > CACHE_MAX_AGE_MS) return null
  return hit
}

/** Wipe cached data - called on sign-out so one person's data never shows
 * under the next person's session on a shared device. */
export async function clearCache(): Promise<void> {
  await tx(STORE_CACHE, 'readwrite', (s) => s.clear())
}

/* ── Outbox ─────────────────────────────────────────────────────────────── */

const listeners = new Set<(entries: OutboxEntry[]) => void>()

export function onOutboxChange(fn: (entries: OutboxEntry[]) => void): () => void {
  listeners.add(fn)
  void listOutbox().then(fn)
  return () => { listeners.delete(fn) }
}

async function notify(): Promise<void> {
  const entries = await listOutbox()
  listeners.forEach((fn) => fn(entries))
}

export async function listOutbox(): Promise<OutboxEntry[]> {
  const all = await tx<OutboxEntry[]>(STORE_OUTBOX, 'readonly', (s) => s.getAll())
  return (all ?? []).sort((a, b) => a.queuedAt - b.queuedAt)
}

export async function queueWrite(
  entry: Omit<OutboxEntry, 'id' | 'queuedAt' | 'attempts'>,
): Promise<OutboxEntry> {
  const full: OutboxEntry = {
    ...entry,
    id: newId(),
    queuedAt: Date.now(),
    attempts: 0,
  }
  await tx(STORE_OUTBOX, 'readwrite', (s) => s.put(full))
  void notify()
  return full
}

export async function removeFromOutbox(id: string): Promise<void> {
  await tx(STORE_OUTBOX, 'readwrite', (s) => s.delete(id))
  void notify()
}

async function updateEntry(entry: OutboxEntry): Promise<void> {
  await tx(STORE_OUTBOX, 'readwrite', (s) => s.put(entry))
  void notify()
}

/** crypto.randomUUID is unavailable on older Android WebViews and on any
 * non-secure origin, so fall back to a timestamp+random id. Uniqueness only
 * has to hold within one device's outbox. */
function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  } catch { /* fall through */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/* ── Connectivity ───────────────────────────────────────────────────────── */

export function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false
}

export function onConnectivityChange(fn: (online: boolean) => void): () => void {
  const up = () => fn(true)
  const down = () => fn(false)
  window.addEventListener('online', up)
  window.addEventListener('offline', down)
  return () => {
    window.removeEventListener('online', up)
    window.removeEventListener('offline', down)
  }
}

/** True when a failed fetch was a transport failure (no network, DNS, CORS
 * preflight death) rather than a real HTTP response. `fetch` rejects with a
 * TypeError in that case, which is the only signal the platform gives us. */
export function isNetworkFailure(err: unknown): boolean {
  return err instanceof TypeError
}

/* ── Replay ─────────────────────────────────────────────────────────────── */

/** Max attempts before an entry stops being retried automatically. It stays
 * in the outbox so the person can see it failed and retry by hand, rather
 * than being deleted silently. */
const MAX_ATTEMPTS = 8

let flushing = false

/**
 * Send everything queued, oldest first. Stops at the first transport failure
 * so ordering is preserved (a later write may depend on an earlier one).
 *
 * `send` is injected rather than imported to avoid a cycle with lib/api.
 */
export async function flushOutbox(
  send: (entry: OutboxEntry) => Promise<void>,
): Promise<{ sent: number; failed: number }> {
  if (flushing || !isOnline()) return { sent: 0, failed: 0 }
  flushing = true
  let sent = 0
  let failed = 0
  try {
    for (const entry of await listOutbox()) {
      if (entry.attempts >= MAX_ATTEMPTS) { failed += 1; continue }
      try {
        await send(entry)
        await removeFromOutbox(entry.id)
        sent += 1
      } catch (err) {
        if (isNetworkFailure(err)) {
          // Still offline - leave this and everything after it untouched.
          await updateEntry({ ...entry, attempts: entry.attempts + 1 })
          break
        }
        // A real HTTP error: the server saw it and refused. Retrying will not
        // help, so record why and move on rather than blocking the queue.
        await updateEntry({
          ...entry,
          attempts: MAX_ATTEMPTS,
          lastError: err instanceof Error ? err.message : 'Rejected by the server',
        })
        failed += 1
      }
    }
  } finally {
    flushing = false
  }
  return { sent, failed }
}
